import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from "@/components/ui/use-toast";
import Layout from '@/components/Layout';
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Link } from 'react-router-dom';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTheme } from "@/components/theme-provider";
import { Switch } from "@/components/ui/switch";

// Using the same color options as in the onboarding
const colorOptions = [
    { name: 'Slate', value: 'bg-slate-600' },
    { name: 'Red', value: 'bg-red-600' },
    { name: 'Orange', value: 'bg-orange-600' },
    { name: 'Amber', value: 'bg-amber-600' },
    { name: 'Yellow', value: 'bg-yellow-600' },
    { name: 'Lime', value: 'bg-lime-600' },
    { name: 'Green', value: 'bg-green-600' },
    { name: 'Emerald', value: 'bg-emerald-600' },
    { name: 'Teal', value: 'bg-teal-600' },
    { name: 'Cyan', value: 'bg-cyan-600' },
    { name: 'Blue', value: 'bg-blue-600' },
    { name: 'Indigo', value: 'bg-indigo-600' },
    { name: 'Violet', value: 'bg-violet-600' },
    { name: 'Purple', value: 'bg-purple-600' },
    { name: 'Fuchsia', value: 'bg-fuchsia-600' },
    { name: 'Pink', value: 'bg-pink-600' }
];

const timezones = [
    { label: "Pacific Time (PT)", value: "America/Los_Angeles" },
    { label: "Mountain Time (MT)", value: "America/Denver" },
    { label: "Central Time (CT)", value: "America/Chicago" },
    { label: "Eastern Time (ET)", value: "America/New_York" },
    { label: "Atlantic Time (AT)", value: "America/Halifax" },
    { label: "British Time (BST/GMT)", value: "Europe/London" },
    { label: "Central European Time (CET)", value: "Europe/Paris" },
    { label: "Eastern European Time (EET)", value: "Europe/Helsinki" },
    { label: "India (IST)", value: "Asia/Kolkata" },
    { label: "Singapore/Hong Kong", value: "Asia/Singapore" },
    { label: "Japan (JST)", value: "Asia/Tokyo" },
    { label: "Australia Eastern (AEST)", value: "Australia/Sydney" },
    { label: "New Zealand (NZST)", value: "Pacific/Auckland" },
    { label: "UTC (Universal Time)", value: "UTC" }, // UTC is the default timezone
];

const Preferences = () => {
    const { user, setUser, axiosInstance } = useAuth();
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [fetchingProfile, setFetchingProfile] = useState(true);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        avatarColor: '',
        timezone: '',
        emailNotificationsEnabled: true
    });
    const [hasChanges, setHasChanges] = useState(false);
    const { theme, setTheme } = useTheme();

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await axiosInstance.get('/users/profile');
                const profileData = {
                    name: response.data.name || '',
                    email: response.data.email || '',
                    avatarColor: response.data.avatarColor || 'bg-neutral-600',
                    timezone: response.data.timezone || 'UTC',
                    emailNotificationsEnabled: response.data.emailNotificationsEnabled || true
                };
                setFormData(profileData);
                setUser(response.data);
                setHasChanges(false);
            } catch (error) {
                console.error('Error fetching profile:', error);
                toast({
                    description: "Failed to load profile data",
                    variant: "destructive"
                });
            } finally {
                setFetchingProfile(false);
            }
        };

        fetchProfile();
    }, []);

    const getInitials = (name) => {
        if (!name) return '';
        const nameParts = name.trim().split(' ');
        const first = nameParts[0][0] || '';
        const last = nameParts.length > 1 ? nameParts[nameParts.length - 1][0] : '';
        return (first + last).toUpperCase();
    };

    const checkForChanges = (newFormData) => {
        return (
            newFormData.name !== user.name ||
            newFormData.avatarColor !== user.avatarColor ||
            newFormData.timezone !== user.timezone ||
            newFormData.emailNotificationsEnabled !== user.emailNotificationsEnabled
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await axiosInstance.patch('/users/profile', {
                name: formData.name,
                avatarColor: formData.avatarColor,
                timezone: formData.timezone,
                emailNotificationsEnabled: formData.emailNotificationsEnabled
            });

            setUser(response.data);
            setHasChanges(false);
            
            toast({
                description: "Profile updated successfully",
            });
        } catch (error) {
            console.error('Error updating profile:', error);
            toast({
                description: "Failed to update profile",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout>
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="space-y-6"
            >
                <div className="space-y-4">
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink href="/today">
                                    Today
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbPage>Preferences</BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>

                    <div>
                        <h1 className="text-3xl font-bold">Preferences</h1>
                        <p className="text-muted-foreground">
                            Manage your personal settings and preferences
                        </p>
                    </div>
                </div>

                {fetchingProfile ? (
                    <div className="flex items-center justify-center min-h-[400px]">
                        <div className="animate-spin">
                            <svg className="h-8 w-8 text-muted-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="mt-16 pb-8">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between bg-background sticky top-0 z-10 border-b rounded-t-lg">
                                <div>
                                    <CardTitle>Profile</CardTitle>
                                    <CardDescription className="text-xs text-muted-foreground mt-2">
                                        Update your personal information
                                    </CardDescription>
                                </div>
                                {hasChanges && (
                                    <div>
                                        <Button
                                            type="submit"
                                            disabled={loading}
                                            onClick={handleSubmit}
                                            className="shadow-lg"
                                        >
                                            {loading ? "Saving..." : "Save Changes"}
                                        </Button>
                                    </div>
                                )}
                            </CardHeader>
                            <CardContent className="space-y-8 pt-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        value={formData.email}
                                        disabled
                                        className="max-w-md"
                                    />
                                    <p className="text-sm text-muted-foreground">
                                        Your email address is used for notifications and sign-in
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="timezone">Timezone</Label>
                                    <Select
                                        value={formData.timezone}
                                        onValueChange={(value) => {
                                            const newFormData = { ...formData, timezone: value };
                                            setFormData(newFormData);
                                            setHasChanges(checkForChanges(newFormData));
                                        }}
                                    >
                                        <SelectTrigger className="max-w-md">
                                            <SelectValue placeholder="Select your timezone" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {timezones.map((tz) => (
                                                <SelectItem key={tz.value} value={tz.value}>
                                                    {tz.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-sm text-muted-foreground">
                                        Your timezone is used to display dates and times correctly
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="theme">Theme</Label>
                                    <Select
                                        value={theme}
                                        onValueChange={(value) => setTheme(value)}
                                    >
                                        <SelectTrigger className="max-w-md">
                                            <SelectValue placeholder="Select theme" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="light">Light</SelectItem>
                                            <SelectItem value="dark">Dark</SelectItem>
                                            <SelectItem value="system">System</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <p className="text-sm text-muted-foreground">
                                        Choose your preferred theme for the application
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex items-center justify-between max-w-2xl">
                                        <Label htmlFor="emailNotifications">Email Notifications</Label>
                                        <Switch
                                            id="emailNotifications"
                                            checked={formData.emailNotificationsEnabled}
                                            onCheckedChange={(checked) => {
                                                const newFormData = { ...formData, emailNotificationsEnabled: checked };
                                                setFormData(newFormData);
                                                setHasChanges(checkForChanges(newFormData));
                                            }}
                                        />
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        Receive email notifications for task assignments, mentions, and updates
                                    </p>
                                </div>

                                <Separator />

                                <div className="space-y-2">
                                    <Label htmlFor="name">Display Name</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => {
                                            const newFormData = { ...formData, name: e.target.value };
                                            setFormData(newFormData);
                                            setHasChanges(checkForChanges(newFormData));
                                        }}
                                        className="max-w-md"
                                    />
                                </div>

                                <div className="space-y-4">
                                    <Label>Avatar</Label>
                                    <div className="flex items-center gap-3">
                                        <Avatar className="h-12 w-12">
                                            <AvatarFallback className={`text-xl ${formData.avatarColor} text-neutral-200`}>
                                                {getInitials(formData.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="text-sm text-muted-foreground">
                                            Choose a color for your avatar
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap gap-3">
                                        {colorOptions.map((color) => (
                                            <button
                                                key={color.value}
                                                type="button"
                                                className={`w-7 h-7 rounded-full ${color.value} transition-all hover:scale-110 p-1 ${
                                                    formData.avatarColor === color.value ? 'ring-2 ring-offset-2 ring-offset-background ring-ring' : ''
                                                }`}
                                                onClick={() => {
                                                    const newFormData = { ...formData, avatarColor: color.value };
                                                    setFormData(newFormData);
                                                    setHasChanges(checkForChanges(newFormData));
                                                }}
                                                title={color.name}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </form>
                )}
            </motion.div>
        </Layout>
    );
};

export default Preferences; 